import { z } from 'zod';
import { accountSchema, baseSchemaShape, profileStatsSchema, profileDataSchema, idSchemaShape, roleSchema, profileSchema, groupDataSchema, modelSchema, groupRoleSchema, mentionDataSchema, reactionDataSchema, answerSchema, rsvpSchema, postDataUnionSchema, postTypeSchema, visibilityTypeSchema, applicationDataSchema, hashtagSchema, accountInteractionsSchema, inviteLinkSchema, reportSchema, groupSchema, jamRecordingSchema, accessTokenSchema, scopeSchema, } from './models.js';
import { jsonSchema } from './utils.js';
export const groupWithMetaSchema = groupDataSchema.extend({
    ...baseSchemaShape,
    ...idSchemaShape,
    membersCount: z.number(),
    lastPostAt: z.string().datetime().nullable().optional(),
});
export const adminGroupSchema = groupWithMetaSchema.extend({
    postsCount: z.number(),
    owners: profileSchema.array(),
});
export const membershipSchema = groupRoleSchema.extend({
    group: modelSchema(groupWithMetaSchema),
});
export const profileWithMetaSchema = profileDataSchema.extend({
    ...baseSchemaShape,
    ...idSchemaShape,
    roles: roleSchema.array(),
    followers: profileSchema.array(),
    following: profileSchema.array(),
    controllers: accountSchema.array(),
    memberships: membershipSchema.array(),
    profileStats: profileStatsSchema,
    blockingIds: z.array(z.string()).default([]),
    blockedByIds: z.array(z.string()).default([]),
    followedHashtags: hashtagSchema.array().default([]),
});
export const memberExportStatsSchema = z.object({
    postsCount: z.number(),
    reactionsCount: z.number(),
    lastSeen: z.iso.datetime().nullable(),
});
export const accessTokenWithMetaSchema = accessTokenSchema.extend({
    profile: profileWithMetaSchema,
    scopes: z.array(scopeSchema).optional(),
});
export const mentionWithProfileSchema = mentionDataSchema.extend({
    profile: profileWithMetaSchema,
});
const reactionWithProfileSchema = reactionDataSchema.extend({
    profile: profileWithMetaSchema,
});
const repostWithProfileSchema = z.object({
    profile: profileWithMetaSchema,
    createdAt: z.string().datetime(),
});
const _answerWithProfileSchema = answerSchema.extend({
    profile: profileWithMetaSchema,
});
const rsvpWithMetaSchema = rsvpSchema.extend({
    profile: profileWithMetaSchema,
    createdAt: z.string().datetime(),
});
export const entryWithProfileSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('create'),
        data: postDataUnionSchema,
        profile: profileWithMetaSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('edit'),
        data: postDataUnionSchema,
        profile: profileWithMetaSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('delete'),
        data: z.any(),
        profile: profileWithMetaSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('answer'),
        data: answerSchema,
        profile: profileWithMetaSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('rsvp'),
        data: rsvpSchema,
        profile: profileWithMetaSchema,
        createdAt: z.string().datetime(),
    }),
]);
export const replyPostSchema = z.object({
    id: z.string(),
    type: postTypeSchema,
    profile: profileWithMetaSchema,
    data: postDataUnionSchema,
    creatorId: z.string(),
    entries: entryWithProfileSchema.array(),
    createdAt: z.string().datetime(),
    deletedAt: z.string().datetime().optional().nullable(),
    visibility: visibilityTypeSchema,
    group: groupWithMetaSchema.optional().nullable(),
    audience: z.array(profileWithMetaSchema).optional().nullable(),
    seen: z.boolean().optional(),
    inReplyToId: z.string().optional().nullable(),
    hidden: z.boolean().optional(),
});
const basePostSchema = replyPostSchema.extend({
    repostCount: z.number(),
    replyCount: z.number(),
    reactions: reactionWithProfileSchema.array(),
    /** Capped lean list of who reposted (most recent). Count stays in repostCount. */
    reposts: repostWithProfileSchema.array(),
    application: applicationDataSchema.optional(),
    mentions: z.array(mentionWithProfileSchema),
    tags: z.array(hashtagSchema),
    groupId: z.string().optional().nullable(),
    groupDiscoverable: z.boolean().optional().nullable(),
    updatedAt: z.string().datetime(),
    accountInteractions: accountInteractionsSchema.optional(),
    rsvps: rsvpWithMetaSchema.array(),
    seen: z.boolean().optional(),
    occurrenceRecurrenceId: z.string().datetime().optional(),
    occurrenceStart: z.string().datetime().optional(),
    occurrenceEnd: z.string().datetime().optional(),
});
const withReplyPostSchema = basePostSchema.extend({
    inReplyToId: z.string().optional().nullable(),
    replyTo: replyPostSchema.optional().nullable(),
});
export const postWithMetaSchema = withReplyPostSchema
    .extend({
    repost: basePostSchema.optional().nullable(),
})
    .openapi('Post');
export const expandedNotificationSchema = z.object({
    id: z.string().uuid(),
    profileId: z.string().uuid(),
    pushHandled: z.boolean().optional(),
    emailHandled: z.boolean().optional(),
    type: z.string(),
    seen: z.boolean().optional(),
    read: z.boolean().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    recipientProfile: profileWithMetaSchema,
    senderProfile: profileWithMetaSchema.nullable().optional(),
    group: groupSchema.nullable().optional(),
    post: postWithMetaSchema.nullable().optional(),
    data: jsonSchema.optional(),
});
export const groupMemberSchema = groupRoleSchema.extend({
    profile: profileWithMetaSchema,
});
export const inviteLinkWithMetaSchema = inviteLinkSchema.extend({
    profile: profileWithMetaSchema,
    redemptions: profileWithMetaSchema.array(),
    /** Resolved from `groupIds` by the mapping so the UI can show group names. */
    groups: groupWithMetaSchema.array(),
});
export const postContextSchema = z.object({
    ancestors: postWithMetaSchema.array(),
    descendants: postWithMetaSchema.array(),
});
export const reportWithMetaSchema = reportSchema.extend({
    reporterProfile: profileWithMetaSchema,
    reportedProfile: profileWithMetaSchema,
    reportedPosts: z.array(postWithMetaSchema),
});
export const jamRecordingWithMetaSchema = jamRecordingSchema.extend({
    profile: profileWithMetaSchema,
    post: postWithMetaSchema,
});
